import { useCallback, useState } from 'react';
import { useSupabase } from './useSupabase';
import type { CrewJoinRequest } from '../types/crew-join-request';

// ---------- utilitaire de normalisation ----------

function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // retire les accents
    .replace(/[^a-z0-9]/g, ''); // retire tout sauf alphanumériques
}

function isSimilarName(a: string, b: string): boolean {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  // même nom normalisé, ou l'un commence par l'autre (gère prénoms courts/diminutifs)
  return na === nb || na.startsWith(nb) || nb.startsWith(na);
}

// ---------- types ----------

export interface PotentialDuplicate {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  birth_date: string | null;
  city: string | null;
}

export interface DuplicateCheckResult {
  contacts: PotentialDuplicate[];
  /** true = même email → impossible de créer un second contact avec cet email */
  hasEmailConflict: boolean;
}

// ---------- hook ----------

export function useCrewJoinRequests() {
  const supabase = useSupabase();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<CrewJoinRequest[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('crew_join_requests')
        .select('*, crew:crews(id, name, crew_number)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as CrewJoinRequest[]) || []);
    } catch (err) {
      console.error('[useCrewJoinRequests] fetchAll error:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const deleteRequest = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await supabase.from('crew_join_requests').delete().eq('id', id);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('[useCrewJoinRequests] deleteRequest error:', err);
        return false;
      }
    },
    [supabase]
  );

  /**
   * Détecte les contacts potentiellement en doublon avec la demande.
   * Priorité : même email → retourne immédiatement avec hasEmailConflict=true.
   * Sinon : même nom de famille (ilike) + prénom similaire (normalisé côté client).
   */
  const findPotentialDuplicates = useCallback(
    async (request: CrewJoinRequest): Promise<DuplicateCheckResult> => {
      // 1. Recherche par email (insensible à la casse via ilike)
      const { data: emailMatches } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, birth_date, city')
        .ilike('email', request.email);

      if (emailMatches && emailMatches.length > 0) {
        return { contacts: emailMatches as PotentialDuplicate[], hasEmailConflict: true };
      }

      // 2. Recherche par nom de famille (ilike pour case-insensitive)
      const { data: lastNameMatches } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, email, phone, birth_date, city')
        .ilike('last_name', request.last_name);

      // Filtrage côté client : prénom similaire (insensible accents + casse)
      const similar = (lastNameMatches || []).filter((c: any) =>
        isSimilarName(c.first_name ?? '', request.first_name)
      );

      return { contacts: similar as PotentialDuplicate[], hasEmailConflict: false };
    },
    [supabase]
  );

  /**
   * Attache la demande à l'équipage, crée ou réutilise un contact, puis supprime la demande.
   * - existingContactId fourni  → met à jour ce contact et l'attache à l'équipage
   * - existingContactId absent  → crée un nouveau contact puis l'attache
   * Toute l'opération est atomique côté serveur (RPC accept_crew_join_request,
   * migration 00182) : verrous demande + équipage, plafond 4L, aucun état partiel.
   * Retourne le crew_id en cas de succès (pour redirection), null sinon.
   */
  const attachToCrewAndDelete = useCallback(
    async (request: CrewJoinRequest, existingContactId?: string): Promise<string | null> => {
      const { data, error } = await supabase.rpc('accept_crew_join_request', {
        p_request_id: request.id,
        p_existing_contact_id: existingContactId ?? null,
      });

      if (error) {
        console.error('[useCrewJoinRequests] attachToCrewAndDelete error:', error);
        return null;
      }

      return data as string;
    },
    [supabase]
  );

  return {
    requests,
    loading,
    fetchAll,
    deleteRequest,
    findPotentialDuplicates,
    attachToCrewAndDelete,
  };
}
